import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Search, Loader2, Ban, X, ChevronUp, ChevronDown, ChevronsUpDown, CalendarCheck, MailCheck, Download, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { valueLabel } from "@/i18n";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import BulkReject from "@/components/BulkReject";

export const StatusBadge = ({ status }) => {
  const { t } = useTranslation();
  const map = {
    new: "bg-muted text-muted-foreground border-border",
    shortlisted: "bg-accent/10 text-accent border-accent/20",
    recommended: "bg-primary/10 text-primary border-primary/20",
    rejected: "bg-destructive/10 text-destructive border-destructive/20",
  };
  return (
    <span className={`px-3 py-1 rounded-full text-xs font-medium border whitespace-nowrap ${map[status] || map.new}`}>
      {t(`status.${status || "new"}`)}
    </span>
  );
};

const TABS = ["all", "new", "shortlisted", "recommended", "rejected"];

export default function Applications() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState({});
  const [rejectOpen, setRejectOpen] = useState(false);
  const [sortCol, setSortCol] = useState(null); // {key, dir}
  const [aiById, setAiById] = useState({}); // second reader: {overall, gap, integrity} per application
  const [onlyFlagged, setOnlyFlagged] = useState(false);

  useEffect(() => {
    api.get("/ai-reviews").then((r) => {
      const m = {};
      r.data.applications.forEach((a) => {
        if (a.ai) m[a.id] = { overall: a.ai.overall, gap: a.evaluators ? a.ai.overall - a.evaluators.overall : null,
          integrity: (a.ai.result?.integrity || []).length };
      });
      setAiById(m);
    }).catch(() => {});
  }, []);
  const hasAi = Object.keys(aiById).length > 0;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/applications", { params: { status, search } });
      setApps(res.data);
    } finally {
      setLoading(false);
    }
  }, [status, search]);

  useEffect(() => {
    const t = setTimeout(load, search ? 300 : 0);
    return () => clearTimeout(t);
  }, [load, search]);

  const selectedIds = Object.keys(selected).filter((id) => selected[id]);
  const toggle = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));
  const clearSelection = () => setSelected({});
  const allChecked = apps.length > 0 && apps.every((a) => selected[a.id]);
  const toggleAll = () => {
    if (allChecked) { clearSelection(); }
    else { const s = {}; apps.forEach((a) => { s[a.id] = true; }); setSelected(s); }
  };
  const afterReject = () => { clearSelection(); load(); };

  const exportJson = async () => {
    const res = await api.get("/admin/export");
    const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `penda-data-export-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const COL_ACCESSORS = {
    applicant: (a) => `${a.name || ""} ${a.surname || ""}`.trim().toLowerCase(),
    university: (a) => (a.university || "").toLowerCase(),
    field: (a) => (a.field_of_study || "").toLowerCase(),
    gpa: (a) => parseFloat(a.gpa) || 0,
    status: (a) => (a.status || ""),
    ai: (a) => aiById[a.id]?.overall ?? -1,
    gap: (a) => (aiById[a.id]?.gap != null ? Math.abs(aiById[a.id].gap) : -1),
  };

  const requestSort = (key) => {
    setSortCol((prev) => {
      if (prev?.key === key) return { key, dir: prev.dir === "asc" ? "desc" : "asc" };
      return { key, dir: "asc" };
    });
  };

  const shownApps = onlyFlagged ? apps.filter((a) => aiById[a.id]?.integrity) : apps;
  const sortedApps = (() => {
    if (!sortCol) return shownApps;
    const acc = COL_ACCESSORS[sortCol.key];
    const arr = [...shownApps].sort((a, b) => {
      const va = acc(a), vb = acc(b);
      if (va < vb) return sortCol.dir === "asc" ? -1 : 1;
      if (va > vb) return sortCol.dir === "asc" ? 1 : -1;
      return 0;
    });
    return arr;
  })();

  const SortHead = ({ label, colKey, className = "" }) => {
    const active = sortCol?.key === colKey;
    return (
      <th className={`py-4 px-6 font-medium ${className}`}>
        <button onClick={() => requestSort(colKey)} className="inline-flex items-center gap-1.5 hover:text-foreground transition-colors uppercase tracking-wider" data-testid={`sort-${colKey}`}>
          {label}
          {active ? (sortCol.dir === "asc" ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />) : <ChevronsUpDown className="w-3.5 h-3.5 opacity-40" />}
        </button>
      </th>
    );
  };

  return (
    <div className="animate-fade-in">
      <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
        <div>
          <p className="text-sm text-muted-foreground uppercase tracking-[0.2em] mb-1">{t("admin.applications.eyebrow")}</p>
          <h1 className="font-heading text-3xl font-semibold tracking-tight">
            {t("admin.applications.title")}
            {!loading && <span className="text-muted-foreground font-normal text-lg ml-3" data-testid="results-count">{t("admin.applications.count", { count: sortedApps.length })}</span>}
          </h1>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={exportJson} className="h-11" data-testid="export-json-btn">
            <Download className="w-4 h-4 mr-2" /> {t("admin.applications.export_json")}
          </Button>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-4 mb-6">
        <div className="flex gap-1 bg-muted p-1 rounded-lg">
          {TABS.map((key) => (
            <button key={key} onClick={() => setStatus(key)}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${status === key ? "bg-card shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}
              data-testid={`tab-${key}`}>
              {t(`status.${key}`)}
            </button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("admin.applications.search")} className="pl-10 h-11" data-testid="search-input" />
        </div>
        {hasAi && (
          <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer" data-testid="filter-integrity">
            <Checkbox checked={onlyFlagged} onCheckedChange={(v) => setOnlyFlagged(!!v)} />
            <AlertTriangle className="w-4 h-4 text-amber-600" /> {t("admin.applications.only_integrity")}
          </label>
        )}
      </div>

      {selectedIds.length > 0 && (
        <div className="flex items-center justify-between bg-secondary border border-primary/10 rounded-lg px-4 py-3 mb-4" data-testid="bulk-bar">
          <span className="text-sm font-medium">{t("admin.applications.selected", { count: selectedIds.length })}</span>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="destructive" onClick={() => setRejectOpen(true)} data-testid="bulk-reject-btn">
              <Ban className="w-4 h-4 mr-2" /> {t("admin.applications.reject_notify")}
            </Button>
            <Button size="sm" variant="ghost" onClick={clearSelection} data-testid="clear-selection-btn">
              <X className="w-4 h-4 mr-1" /> {t("common.clear")}
            </Button>
          </div>
        </div>
      )}

      <div className="bg-card border border-border rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        ) : apps.length === 0 ? (
          <div className="text-center py-20 text-muted-foreground text-sm">{t("admin.applications.none")}</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/50 text-muted-foreground text-xs uppercase tracking-wider text-left">
                <th className="py-4 pl-6 pr-2 w-10"><Checkbox checked={allChecked} onCheckedChange={toggleAll} data-testid="select-all" /></th>
                <SortHead label={t("admin.applications.col_applicant")} colKey="applicant" />
                <SortHead label={t("admin.applications.col_university")} colKey="university" />
                <SortHead label={t("admin.applications.col_field")} colKey="field" />
                <SortHead label={t("admin.applications.col_gpa")} colKey="gpa" />
                <SortHead label={t("admin.applications.col_status")} colKey="status" />
                {hasAi && <SortHead label={t("admin.applications.col_ai")} colKey="ai" className="!px-3" />}
                {hasAi && <SortHead label={t("admin.applications.col_gap")} colKey="gap" className="!pl-3 !pr-6" />}
              </tr>
            </thead>
            <tbody>
              {sortedApps.map((a) => (
                <tr key={a.id}
                  className={`border-t border-border transition-colors cursor-pointer ${selected[a.id] ? "bg-primary/5" : "hover:bg-muted/30"}`} data-testid={`app-row-${a.id}`}>
                  <td className="py-4 pl-6 pr-2" onClick={(e) => e.stopPropagation()}>
                    <Checkbox checked={!!selected[a.id]} onCheckedChange={() => toggle(a.id)} data-testid={`select-${a.id}`} />
                  </td>
                  <td className="py-4 px-6" onClick={() => navigate(`/admin/applications/${a.id}`)}>
                    <p className="font-medium text-foreground">{a.name} {a.surname}</p>
                    <p className="text-xs text-muted-foreground">{a.email}</p>
                  </td>
                  <td className="py-4 px-6 text-muted-foreground max-w-[170px] truncate" onClick={() => navigate(`/admin/applications/${a.id}`)}>{valueLabel(a.university) || "—"}</td>
                  <td className="py-4 px-6 text-muted-foreground max-w-[160px] truncate" onClick={() => navigate(`/admin/applications/${a.id}`)}>{valueLabel(a.field_of_study)?.split(" (")[0] || "—"}</td>
                  <td className="py-4 px-6" onClick={() => navigate(`/admin/applications/${a.id}`)}>{a.gpa || "—"}</td>
                  <td className="py-4 px-6" onClick={() => navigate(`/admin/applications/${a.id}`)}>
                    <div className="flex items-center gap-2">
                      <StatusBadge status={a.status} />
                      {a.interview && (
                        <span className="inline-flex items-center gap-1 text-[10px] text-accent" title={t("admin.applications.invited")} data-testid={`invited-${a.id}`}>
                          <CalendarCheck className="w-3.5 h-3.5" />
                        </span>
                      )}
                      {a.status === "rejected" && a.rejection && (
                        <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground" title={t("admin.applications.notified")} data-testid={`notified-${a.id}`}>
                          <MailCheck className="w-3.5 h-3.5" />
                        </span>
                      )}
                    </div>
                  </td>
                  {hasAi && (
                    <td className="py-4 px-3 whitespace-nowrap" onClick={() => navigate(`/admin/applications/${a.id}`)}>
                      {aiById[a.id] ? (
                        <span className="inline-flex items-center gap-1.5">
                          <span className="font-medium">{aiById[a.id].overall.toFixed(1)}</span>
                          {aiById[a.id].integrity > 0 && (
                            <span className="inline-flex items-center gap-0.5 text-amber-700 text-xs" title={t("admin.applications.integrity_flags", { count: aiById[a.id].integrity })} data-testid={`integrity-${a.id}`}>
                              <AlertTriangle className="w-3.5 h-3.5" />{aiById[a.id].integrity}
                            </span>
                          )}
                        </span>
                      ) : <span className="text-muted-foreground">—</span>}
                    </td>
                  )}
                  {hasAi && (
                    <td className="py-4 pl-3 pr-6 whitespace-nowrap" onClick={() => navigate(`/admin/applications/${a.id}`)}>
                      {aiById[a.id]?.gap != null ? (
                        <span className={Math.abs(aiById[a.id].gap) >= 3 ? "text-destructive font-semibold" : Math.abs(aiById[a.id].gap) >= 1.5 ? "text-accent font-medium" : "text-muted-foreground"}>
                          {aiById[a.id].gap > 0 ? "+" : aiById[a.id].gap < 0 ? "−" : ""}{Math.abs(aiById[a.id].gap).toFixed(1)}
                        </span>
                      ) : <span className="text-muted-foreground">—</span>}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <BulkReject selectedIds={selectedIds} open={rejectOpen} onOpenChange={setRejectOpen} onDone={afterReject} />
    </div>
  );
}
